/**
 * Sync activity tracker
 *
 * A tiny observable store that reports whether the data layer is currently
 * pulling from or pushing to the Mon Ami server, and how the last attempt
 * ended. The event listener wraps its bulk refresh/sync handlers in
 * `trackSyncActivity`; UI (the global SyncStatusBanner, Settings > Sync)
 * subscribes so users can see that work is happening — and when it has
 * finished — without ever needing a manual "sync" button.
 *
 * Overlapping operations are counted, so the status only returns to a
 * terminal state once every in-flight operation has settled.
 */

import { getItem, setItem } from './services/application_session_service';

export type SyncActivityStatus = 'idle' | 'syncing' | 'success' | 'error';

export interface SyncActivity {
  status: SyncActivityStatus;
  /** Number of tracked operations currently running. */
  inFlight: number;
  /** When the most recent operation started. */
  startedAt: Date | null;
  /** When the most recent operation finished (success or error). */
  finishedAt: Date | null;
  /** When the last operation completed successfully. Persisted across reloads. */
  lastSuccessAt: Date | null;
  /** The error from the most recent failed operation, cleared on the next success. */
  lastError: Error | null;
  /**
   * Documents the last push sweep could not upload (sync errors such as a
   * permission problem). Reported by the sweep itself, so it is accurate at
   * the moment the sweep finishes rather than read from a live count.
   */
  leftBehind: number;
  /** Contacts the last push sweep could not upload. */
  leftBehindContacts: number;
  /**
   * Whether the current (or last) batch should be shown to the user as
   * progress and completion. True when any operation in the batch was a
   * reconnect or a manual request; background refreshes stay silent.
   * Failures are shown regardless.
   */
  visible: boolean;
}

/**
 * Upper bound for one tracked operation. Network legs already time out
 * (fetch-utils, 10s each), so only a stuck IndexedDB operation could hang; this
 * keeps `syncing` from pinning forever and hiding the dashboard warning.
 */
export const SYNC_WATCHDOG_MS = 5 * 60 * 1000;

const LAST_SUCCESS_KEY = 'sync.last_success_at';

type Listener = (activity: SyncActivity) => void;

let activity: SyncActivity = initialActivity();
const listeners = new Set<Listener>();
let hydrated = false;
let hydrating: Promise<void> | null = null;

function initialActivity(): SyncActivity {
  return {
    status: 'idle',
    inFlight: 0,
    startedAt: null,
    finishedAt: null,
    lastSuccessAt: null,
    lastError: null,
    leftBehind: 0,
    leftBehindContacts: 0,
    visible: false,
  };
}

function update(patch: Partial<SyncActivity>): void {
  activity = { ...activity, ...patch };
  listeners.forEach((listener) => listener(activity));
}

/** Called by the document sweep with how many of the documents it attempted are still pending. */
export function reportLeftBehind(count: number): void {
  if (count !== activity.leftBehind) update({ leftBehind: count });
}

/** Called by the contact sweep with how many contacts it could not upload. */
export function reportLeftBehindContacts(count: number): void {
  if (count !== activity.leftBehindContacts) update({ leftBehindContacts: count });
}

/** Current snapshot. Stable reference between updates (safe for useSyncExternalStore). */
export function getSyncActivity(): SyncActivity {
  return activity;
}

/** Subscribe to changes. Returns an unsubscribe function. */
export function subscribeSyncActivity(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export interface TrackSyncActivityOptions {
  /**
   * Failures for which this returns true are "not right now" (no connectivity)
   * rather than a broken sync: the tracker settles back to idle instead of
   * error, so the user is never alarmed about a retry that will happen anyway.
   */
  quiet?: (error: unknown) => boolean;
  /** Show this operation's progress and completion to the user (reconnect, manual). */
  visible?: boolean;
}

/**
 * Run `work` while reporting it as sync activity. Resolves/rejects with the
 * same outcome as `work` so callers can chain as before.
 */
export async function trackSyncActivity<T>(
  work: () => Promise<T>,
  options: TrackSyncActivityOptions = {}
): Promise<T> {
  void hydrateLastSuccess();
  const startingBatch = activity.inFlight === 0;
  update({
    status: 'syncing',
    inFlight: activity.inFlight + 1,
    startedAt: startingBatch ? new Date() : activity.startedAt,
    // Each reported count belongs to the batch being reported on: a
    // document-only sweep must not re-surface a contact failure from earlier.
    ...(startingBatch ? { leftBehind: 0, leftBehindContacts: 0 } : {}),
    // A visible operation makes the whole batch visible; a silent one joining
    // a visible batch does not hide it.
    visible: startingBatch ? Boolean(options.visible) : activity.visible || Boolean(options.visible),
  });

  let watchdog: ReturnType<typeof setTimeout> | undefined;
  const timedOut = new Promise<never>((_, reject) => {
    watchdog = setTimeout(() => reject(new SyncWatchdogError()), SYNC_WATCHDOG_MS);
  });

  try {
    // Keep observing the real work even if the watchdog wins the race:
    // otherwise its later rejection would surface as an unhandled rejection
    // (and be misrouted by the global database-error handler).
    const running = work();
    running.catch(() => {});
    const result = await Promise.race([running, timedOut]);
    settle('ok');
    return result;
  } catch (error) {
    if (options.quiet?.(error)) {
      settle('quiet');
    } else {
      settle('error', error instanceof Error ? error : new Error(String(error)));
    }
    throw error;
  } finally {
    clearTimeout(watchdog);
  }
}

/** Thrown (into the tracker only) when a tracked operation exceeds SYNC_WATCHDOG_MS. */
export class SyncWatchdogError extends Error {
  constructor() {
    super(`Sync did not finish within ${SYNC_WATCHDOG_MS / 60000} minutes`);
    this.name = 'SyncWatchdogError';
  }
}

// Outcome of the current overlapping batch. A real failure anywhere in the
// batch reports as failed once everything settles, even if the last operation
// succeeded; a batch of only quiet (connectivity) failures returns to idle.
let batchFailed = false;
let batchSucceeded = false;

function settle(outcome: 'ok' | 'quiet' | 'error', error?: Error): void {
  const inFlight = Math.max(0, activity.inFlight - 1);
  const now = new Date();
  if (outcome === 'error') batchFailed = true;

  // Each successful operation counts as a successful sync, even if a sibling
  // in the same batch fails and the batch as a whole reports error.
  const successPatch: Partial<SyncActivity> = {};
  if (outcome === 'ok') {
    batchSucceeded = true;
    successPatch.lastSuccessAt = now;
    void persistLastSuccess(now);
  }
  const errorPatch: Partial<SyncActivity> = error ? { lastError: error } : {};

  if (inFlight > 0) {
    // Siblings still running — stay in 'syncing'.
    update({ inFlight, ...successPatch, ...errorPatch });
    return;
  }

  const failed = batchFailed;
  const succeeded = batchSucceeded;
  batchFailed = false;
  batchSucceeded = false;

  if (failed) {
    update({ inFlight, status: 'error', finishedAt: now, ...successPatch, ...errorPatch });
    return;
  }

  if (!succeeded) {
    // Only quiet failures: nothing to report, connectivity will trigger a retry.
    update({ inFlight, status: 'idle', finishedAt: now });
    return;
  }

  update({ inFlight, status: 'success', finishedAt: now, lastError: null, ...successPatch });
}

async function persistLastSuccess(at: Date): Promise<void> {
  try {
    await setItem(LAST_SUCCESS_KEY, at.toISOString());
  } catch {
    // Not critical — only used to show "Last synced …" after a reload.
  }
}

/**
 * Load the persisted last-success time so the UI can show "Last synced …"
 * right after a reload, before any sync has run in this session.
 */
export async function hydrateLastSuccess(): Promise<void> {
  if (hydrated) return;
  if (hydrating) return hydrating;
  hydrating = (async () => {
    try {
      const stored = await getItem<string>(LAST_SUCCESS_KEY);
      if (stored && !activity.lastSuccessAt) {
        update({ lastSuccessAt: new Date(stored) });
      }
      hydrated = true;
    } catch {
      // Not critical — leave `hydrated` false so a later call can retry.
    } finally {
      hydrating = null;
    }
  })();
  return hydrating;
}

/** Test-only: return to the initial idle state. Subscribers are left in place. */
export function resetSyncActivityForTests(): void {
  activity = initialActivity();
  hydrated = false;
  hydrating = null;
  batchFailed = false;
  batchSucceeded = false;
}
